import React, { useEffect } from 'react';
import {
  VStack,
  SimpleGrid,
  FormControl,
  FormLabel,
  Input,
  Flex,
  Button,
  Box,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import Card from 'components/card/Card';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  createSystemAxis,
  updateSystemAxis,
  getSystemAxisListById,
} from 'api/auth.api';

const SystemAxisWeightForm = ({ editId, onSuccess }) => {
  const id = editId?._id;
  const toast = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      name: '',
      axisCode: '',
      defaultElectricity: '',
      defaultPolarity: '',
      note: '',
      weightPriority: '',
      appArea: '',
      temperament: '',
      active: true,
    },
  });

  /* ================= GET BY ID ================= */
  const { data: editData } = useQuery({
    queryKey: ['systemAxisById', id],
    queryFn: () => getSystemAxisListById(id),
    enabled: !!id,
  });

  useEffect(() => {
    if (editData?.data?.result) {
      const d = editData.data.result;
      reset({
        name: d.name,
        axisCode: d.axisCode,
        weightPriority: d.weightPriority,
        defaultElectricity: d.defaultElectricity,
        defaultPolarity: d.defaultPolarity,
        appArea: d.appArea,
        temperament: d.temperament,
        note: d.note,
        active: d.active,
      });
    }
  }, [editData, reset]);

  /* ================= CREATE ================= */
  const createMutation = useMutation({
    mutationFn: createSystemAxis,
    onSuccess: () => {
      toast({
        title: 'System axis created successfully',
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top-center',
      });
      reset();
      onSuccess?.();
    },
    onError: (error) => {
      toast({
        title: 'Failed to create system axis',
        description: error?.response?.data?.message || error?.message,
        status: 'error',
      });
    },
  });

  /* ================= UPDATE ================= */
  const updateMutation = useMutation({
    mutationFn: updateSystemAxis,
    onSuccess: () => {
      toast({
        title: 'System axis updated successfully',
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top-center',
      });
      reset();
      onSuccess?.();
    },
    onError: (error) => {
      toast({
        title: 'Failed to update system axis',
        description: error?.response?.data?.message || error?.message,
        status: 'error',
      });
    },
  });

  /* ================= SUBMIT ================= */
  const submitHandler = (formData) => {
    if (id) {
      updateMutation.mutate({ id, ...formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Box w="100%" p={2}>
      <form onSubmit={handleSubmit(submitHandler)}>
        <Card p={6} border="1px solid" borderColor="gray.200">
          <VStack spacing={4} align="stretch">
            {/* ROW 1 */}
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <FormControl isInvalid={errors.name} isRequired>
                <FormLabel>System Name</FormLabel>
                <Input
                  {...register('name', { required: 'System name is required' })}
                />
                <Text fontSize="xs" color="red.500">
                  {errors.name?.message}
                </Text>
              </FormControl>

              <FormControl isInvalid={errors.axisCode} isRequired>
                <FormLabel>Axis Code</FormLabel>
                <Input
                  {...register('axisCode', {
                    required: 'Axis code is required',
                  })}
                />
                <Text fontSize="xs" color="red.500">
                  {errors.axisCode?.message}
                </Text>
              </FormControl>

              <FormControl isInvalid={errors.weightPriority} isRequired>
                <FormLabel>Weight Priority</FormLabel>
                <Input
                  type="number"
                  {...register('weightPriority', {
                    required: 'Weight priority is required',
                  })}
                />
                <Text fontSize="xs" color="red.500">
                  {errors.weightPriority?.message}
                </Text>
              </FormControl>
            </SimpleGrid>

            {/* ROW 2 */}
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <FormControl isInvalid={errors.defaultElectricity} isRequired>
                <FormLabel>Default Electricity</FormLabel>
                <Input
                  {...register('defaultElectricity', { required: true })}
                />
              </FormControl>

              <FormControl isInvalid={errors.defaultPolarity} isRequired>
                <FormLabel>Default Polarity</FormLabel>
                <Input {...register('defaultPolarity', { required: true })} />
              </FormControl>

              <FormControl isInvalid={errors.temperament} isRequired>
                <FormLabel>Constitution</FormLabel>
                <Input {...register('temperament', { required: true })} />
              </FormControl>
            </SimpleGrid>

            {/* ROW 3 */}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl isInvalid={errors.appArea} isRequired>
                <FormLabel>APP Area</FormLabel>
                <Textarea {...register('appArea', { required: true })} />
              </FormControl>

              <FormControl>
                <FormLabel>Note</FormLabel>
                <Textarea {...register('note')} />
              </FormControl>
            </SimpleGrid>

            {/* SUBMIT */}
            <Flex justify="center">
              <Button
                type="submit"
                colorScheme="teal"
                isLoading={isLoading}
                w="160px"
              >
                {id ? 'Update' : 'Submit'}
              </Button>
            </Flex>
          </VStack>
        </Card>
      </form>
    </Box>
  );
};

export default SystemAxisWeightForm;
